import React, { useEffect, useState } from 'react';
import { Form, InputNumber, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import StandardDrawer from '../../components/common/StandardDrawer';
import * as personnelService from '../../services/personnel/personnelService';

/**
 * OrderComplexityDrawer - Configure wave order entry condition (SKU limit & Item limit)
 */
const OrderComplexityDrawer = ({ visible, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const maxSkuTypes = Form.useWatch('maxSkuTypes', form);
  const maxItemQty = Form.useWatch('maxItemQty', form);

  // Save button is only enabled when both fields have values
  const isSaveDisabled = maxSkuTypes == null || maxItemQty == null;

  useEffect(() => {
    if (visible) {
      fetchConfig();
    }
  }, [visible]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await personnelService.getOrderComplexityConfig();
      if (response.success && response.data) {
        form.setFieldsValue({
          maxSkuTypes: response.data.maxSkuTypes,
          maxItemQty: response.data.maxItemQty,
        });
      }
    } catch (error) {
      console.error('Fetch config failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const requestData = {
        maxSkuTypes: values.maxSkuTypes != null ? values.maxSkuTypes : null,
        maxItemQty: values.maxItemQty != null ? values.maxItemQty : null,
      };

      const response = await personnelService.updateOrderComplexityConfig(requestData);

      if (response.success) {
        message.success(t('personnel.configSuccess'));
        onSuccess();
      } else {
        message.error(response.errorMessage || t('personnel.configFailed'));
      }
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <StandardDrawer
      visible={visible}
      onClose={onClose}
      onOk={handleSubmit}
      title={t('personnel.configWaveOrderEntryCondition')}
      confirmLoading={loading}
      width={480}
      maskClosable={false}
      destroyOnClose={true}
      okDisabled={isSaveDisabled}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ padding: '24px 0' }}
      >
        {/* Function description tip */}
        <div style={{
          background: '#e6f4ff',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8
        }}>
          <ExclamationCircleOutlined style={{ color: '#1677ff', marginTop: 2 }} />
          <span style={{ color: '#0958d9', fontSize: 14 }}>
            {t('personnel.waveOrderEntryConditionDesc')}
          </span>
        </div>

        <Form.Item
          name="maxSkuTypes"
          label={t('personnel.skuLimit')}
          rules={[
            { required: true, message: t('personnel.skuLimitRequired') },
          ]}
        >
          <InputNumber
            min={0}
            max={999}
            precision={0}
            style={{ width: '100%' }}
            placeholder={t('personnel.skuLimitPlaceholder')}
            addonAfter={t('personnel.skuUnit')}
          />
        </Form.Item>

        <Form.Item
          name="maxItemQty"
          label={t('personnel.itemLimit')}
          rules={[
            { required: true, message: t('personnel.itemLimitRequired') },
          ]}
        >
          <InputNumber
            min={0}
            max={9999}
            precision={0}
            style={{ width: '100%' }}
            placeholder={t('personnel.itemLimitPlaceholder')}
            addonAfter={t('personnel.itemUnit')}
          />
        </Form.Item>
      </Form>
    </StandardDrawer>
  );
};

export default OrderComplexityDrawer;
